import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Fonts from "../../ui/global/Fonts";
import Colors from "../../ui/global/Colors";
import { Auth } from '@supabase/auth-ui-react'
import { ThemeSupa } from '@supabase/auth-ui-shared'
import { SupabaseClient } from '@supabase/supabase-js'
import { useLocation } from 'react-router-dom'
import LoadingSpinner from "../../ui/components/LoadingSpinner";
import Logo from "../../ui/components/Logo";

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100svh;
  width: 100%;
  background-color: ${Colors.black};
  padding: 10px;
`;

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 40px auto;
  padding: 20px;
  background-color: ${Colors.white};
  border-radius: 8px;
  border: 1px solid ${Colors.grey1};
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  font-size: 16px;
`;

const Header = styled.h4`
  color: ${Colors.black};
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 24px;
  text-align: center;
  margin: 10px 0 20px 0;
`;

const ErrorMessage = styled.p`
  color: ${Colors.red};
  margin-bottom: 20px;
  font-size: 18px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  gap: 0;
`;

const FooterText = styled.p`
  color: ${Colors.grey6};
  font-family: ui-sans-serif, sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 4px 0;
`;

const FooterLink = styled.a`
  color: ${Colors.grey6};
  font-family: ui-sans-serif, sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 4px 0;
  text-decoration: underline;
  cursor: pointer;

  &:hover, &:active {
    color: ${Colors.grey8};
  }
`;

interface ForgotPasswordProps {
  supabase: SupabaseClient
}

const ForgotPassword = ({ supabase }: ForgotPasswordProps) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // Initialize auth state
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setLoading(false);
      } catch (err: any) {
        console.error('Error initializing auth:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    initializeAuth();
  }, [supabase]);

  const renderAuth = () => (
    <PageWrapper>
      <Logo colorMode="dark" size={36}/> {/* Add Logo component here */}
      <Container>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Header>Reset Password</Header>
        <Auth 
          supabaseClient={supabase} 
          view="forgotten_password"
          providers={[ /* 'google', 'discord', 'github', */ ]}
          theme="default"
          redirectTo={process.env.REACT_APP_FRONTEND_APP_URL + '/reset-password'}
          showLinks={false}
          appearance={{ 
              theme: ThemeSupa,
              variables: {
              default: {
                  colors: {
                  brand: Colors.grey9,
                  brandAccent: Colors.grey8,
                  },
              },
              },
          }}
          localization={{
            variables: {
              forgotten_password: {
                button_label: "Request Reset",
                confirmation_text: "Check your email for the password reset link",
                email_label: "Email address",
                email_input_placeholder: "Your email address",
              },
            }
          }}
        />
        <Footer>
          <FooterText>Don't have an account? <FooterLink href="/signup">Sign up</FooterLink></FooterText>
        </Footer>
      </Container>
    </PageWrapper>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return renderAuth();
};

export default ForgotPassword;